import { ptzaConfig } from '../../controllers/config.js';

class ConfigBindings {

    apply(root = document) {
        if (!root || typeof root.querySelectorAll !== 'function') {
            return false;
        }

        this.applyTextBindings(root);
        this.applyHrefBindings(root);
        this.applyTitleBindings(root);

        return true;
    }

    getElements(root, selector) {
        const elements = Array.from(
            root.querySelectorAll(selector)
        );

        if (
            typeof root.matches === 'function' &&
            root.matches(selector)
        ) {
            elements.unshift(root);
        }

        return elements;
    }

    /*
     * data-ptza-text
     *
     * Ejemplo:
     *
     * <span
     *     data-ptza-text="clientInfo.nombreCliente"
     * >
     *     Nombre SSR
     * </span>
     *
     * Resultado:
     *
     * <span>Nombre desde config.json</span>
     */
    applyTextBindings(root) {
        this.getElements(
            root,
            '[data-ptza-text]'
        ).forEach((element) => {

            const path = element.getAttribute(
                'data-ptza-text'
            );

            const value = ptzaConfig.getValueByPath(
                path,
                undefined
            );

            /*
             * Ruta inexistente:
             * conservar contenido SSR existente.
             */
            if (value === undefined) {
                return;
            }

            /*
             * textContent evita interpretar HTML
             * proveniente de configuración.
             */
            element.textContent =
                value === null
                    ? ''
                    : String(value);
        });
    }

    /*
     * data-ptza-href
     *
     * Ejemplo:
     *
     * <a
     *     data-ptza-href="clientInfo.instagram"
     *     href="#"
     * >
     *     Instagram
     * </a>
     */
    applyHrefBindings(root) {
        this.getElements(
            root,
            '[data-ptza-href]'
        ).forEach((element) => {

            const path = element.getAttribute(
                'data-ptza-href'
            );

            const value = ptzaConfig.getValueByPath(
                path,
                undefined
            );

            /*
             * Ruta inexistente:
             * conservar href SSR existente.
             */
            if (value === undefined) {
                return;
            }

            const href =
                value === null
                    ? ''
                    : String(value).trim();

            /*
             * Un valor vacío explícito en config
             * gana y elimina el destino.
             */
            if (!href) {
                element.removeAttribute('href');
                return;
            }

            /*
             * Defensa frente a esquemas ejecutables.
             */
            if (
                /^(?:javascript|data|vbscript):/i.test(
                    href
                )
            ) {
                element.removeAttribute('href');

                console.warn(
                    `Propuestaza: href rechazado para binding ${path}.`
                );

                return;
            }

            element.setAttribute(
                'href',
                href
            );
        });
    }

    /*
     * data-ptza-title
     *
     * El texto específico de la página permanece
     * definido en el propio HTML.
     *
     * La marca se obtiene dinámicamente desde:
     *
     * clientInfo.nombreCliente
     *
     * Ejemplo:
     *
     * <title data-ptza-title="Tienda">
     *     Tienda
     * </title>
     *
     * Con:
     *
     * clientInfo.nombreCliente = "Pomela"
     *
     * Resultado:
     *
     * Tienda | Pomela
     */
    applyTitleBindings(root) {
        this.getElements(
            root,
            '[data-ptza-title]'
        ).forEach((element) => {

            /*
             * El título particular de la página
             * está definido en el atributo.
             *
             * Si el atributo existe pero está vacío,
             * usamos el contenido SSR como fallback.
             */
            const configuredPageTitle = String(
                element.getAttribute(
                    'data-ptza-title'
                ) ?? ''
            ).trim();

            const ssrPageTitle = String(
                element.textContent ?? ''
            ).trim();

            const pageTitle =
                configuredPageTitle ||
                ssrPageTitle;

            /*
             * La marca siempre proviene de la
             * configuración actual del tenant.
             */
            const brand = String(
                ptzaConfig.getClientInfoValue(
                    'nombreCliente',
                    ''
                ) ?? ''
            ).trim();

            /*
             * No tenemos ninguna información:
             * conservar el title existente.
             */
            if (!pageTitle && !brand) {
                return;
            }

            /*
             * Página + marca.
             */
            if (pageTitle && brand) {
                element.textContent =
                    `${pageTitle} | ${brand}`;

                return;
            }

            /*
             * Si no hay marca configurada,
             * queda un título válido igualmente.
             */
            if (pageTitle) {
                element.textContent =
                    pageTitle;

                return;
            }

            /*
             * Caso extremo:
             * existe marca pero no título de página.
             */
            element.textContent = brand;
        });
    }
}

export const configBindings = new ConfigBindings();